import { useMemo, useState } from "react";
import { Icon } from "../ui/Icon";

interface LocNode { id: string; code: string; name: string; type: string; parentId: string | null }

interface Props {
  locations: LocNode[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

const TYPE_LABELS: Record<string, string> = { uretim_merkezi: "Merkez", uretim_hatti: "Hat" };

interface Node { id: string; code: string; name: string; type: string; depth: number; parentId: string | null; hasChildren: boolean }

/** Kullanıcı yetkili merkez/hat seçici — LocationTreePicker tasarım diliyle (arama + chevron aç/kapa + girinti +
 *  tip rozeti + bordered scroll), ama ÇOK SEÇİMLİ (checkbox ağacı: uretim_merkezi + altındaki uretim_hatti).
 *  Hiç seçim yok = kullanıcı TÜM tesisini görür; merkez seçilirse tüm alt hatları, hat seçilirse yalnız o hat kapsanır. */
export function ProductionScopePicker({ locations, selectedIds, onChange }: Props) {
  const [q, setQ] = useState("");
  // Merkezler varsayılan AÇIK (kullanıcı hatları görsün); chevron ile daraltılabilir.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const nodes = useMemo(() => {
    const idSet = new Set(locations.map((l) => l.id));
    const byParent = new Map<string, LocNode[]>();
    const roots: LocNode[] = [];
    for (const r of locations) {
      // Kök = üstü listede olmayan (merkezlerin parent'ı tesis, listede yok) düğümler.
      if (r.parentId && idSet.has(r.parentId)) { const a = byParent.get(r.parentId) ?? []; a.push(r); byParent.set(r.parentId, a); }
      else roots.push(r);
    }
    const out: Node[] = [];
    const walk = (rows: LocNode[], depth: number, parentId: string | null) => {
      for (const r of [...rows].sort((a, b) => a.name.localeCompare(b.name, "tr"))) {
        const kids = byParent.get(r.id) ?? [];
        out.push({ id: r.id, code: r.code, name: r.name, type: r.type, depth, parentId, hasChildren: kids.length > 0 });
        walk(kids, depth + 1, r.id);
      }
    };
    walk(roots, 0, null);
    return out;
  }, [locations]);

  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const sel = new Set(selectedIds);
  const norm = (s: string) => s.toLocaleLowerCase("tr-TR");
  const search = q.trim();

  const visible = useMemo(() => {
    if (search) {
      const needle = norm(search);
      const show = new Set<string>();
      for (const n of nodes) {
        if (norm(n.name).includes(needle) || norm(n.code).includes(needle)) {
          show.add(n.id);
          let p = n.parentId;
          while (p && byId.has(p)) { show.add(p); p = byId.get(p)!.parentId; }
        }
      }
      return nodes.filter((n) => show.has(n.id));
    }
    // Arama yokken: bir atası daraltılmışsa gizle.
    return nodes.filter((n) => {
      let p = n.parentId;
      while (p && byId.has(p)) { if (collapsed.has(p)) return false; p = byId.get(p)!.parentId; }
      return true;
    });
  }, [nodes, byId, search, collapsed]);

  function toggleCollapse(id: string) { setCollapsed((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }); }
  function toggleSelect(id: string) { const n = new Set(sel); if (n.has(id)) n.delete(id); else n.add(id); onChange([...n]); }

  if (nodes.length === 0) return <div className="help">Bu tesiste üretim merkezi tanımlı değil.</div>;

  return (
    <>
      <input className="form-input" type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Merkez / hat ara… (ad / kod)" style={{ marginBottom: 6 }} />
      <div style={{ border: "1px solid var(--neutral-300)", borderRadius: "var(--radius-md)", maxHeight: 240, overflowY: "auto", padding: 4 }}>
        {search && visible.length === 0 ? (
          <div style={{ padding: "6px 10px", fontSize: 12, color: "var(--neutral-400)" }}>"{search}" ile eşleşen merkez/hat yok.</div>
        ) : visible.map((n) => {
          const checked = sel.has(n.id);
          const isCollapsed = collapsed.has(n.id);
          return (
            <div key={n.id} style={{ display: "flex", alignItems: "center", gap: 2, paddingLeft: n.depth * 18 }}>
              {n.hasChildren && !search ? (
                <button
                  type="button"
                  onClick={() => toggleCollapse(n.id)}
                  aria-label={isCollapsed ? "Genişlet" : "Daralt"}
                  aria-expanded={!isCollapsed}
                  style={{ width: 18, height: 18, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", border: "none", background: "transparent", borderRadius: 4, cursor: "pointer", color: "var(--neutral-500)", padding: 0, transform: isCollapsed ? "none" : "rotate(90deg)", transition: "transform 100ms" }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
                </button>
              ) : (
                <span style={{ width: 18, flexShrink: 0 }} />
              )}
              <button
                type="button"
                onClick={() => toggleSelect(n.id)}
                aria-pressed={checked}
                style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0, textAlign: "left", padding: "5px 8px", border: "none", borderRadius: 6, cursor: "pointer", background: checked ? "var(--primary-subtle)" : "transparent", color: "var(--neutral-800)", fontSize: 13 }}
              >
                <span style={{ width: 15, height: 15, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 4, border: `1.5px solid ${checked ? "var(--primary)" : "var(--neutral-300)"}`, background: checked ? "var(--primary)" : "var(--white)", color: "var(--white)" }}>
                  {checked ? <Icon name="check" size={11} /> : null}
                </span>
                <span style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", padding: "1px 5px", borderRadius: 4, color: "var(--neutral-600)", background: "var(--neutral-100)", whiteSpace: "nowrap" }}>{TYPE_LABELS[n.type] ?? n.type}</span>
                <span style={{ fontWeight: checked ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.name}</span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--neutral-400)", flexShrink: 0 }}>{n.code}</span>
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}
